import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  handoverService,
  rentals,
  cars,
  employeeService
} from "../services/api";
import moment from 'moment';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUser, FaCar, FaClipboardList, FaCalendarAlt, FaGasPump, FaTachometerAlt, FaClipboard, 
  FaImage, FaVideo, FaMicrophone, FaFileUpload, FaArrowRight, FaArrowLeft, FaCheckCircle, FaExclamationTriangle, 
  FaTimesCircle, FaTrash, FaPlusCircle, FaSave, FaPen, FaCamera, FaInfoCircle, FaCheck, FaEraser } from 'react-icons/fa';
import SignatureCanvas from 'react-signature-canvas'

const HandoverForm = ({ isModal = false, onClose = () => {}, onSubmitSuccess = null }) => {
  const { rentalId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rental, setRental] = useState(null);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);  const [rentalsByHandoverType, setPendingRentals] = useState([]);
  const [selectedRental, setSelectedRental] = useState(null);
  const [carModel, setCarModel] = useState(null);
  
  // Admin-specific states
  const [isAdmin, setIsAdmin] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  
    const [formData, setFormData] = useState({
    rental: rentalId || '',
    handoverType: 'pickup', // Default to pickup
    mileage: '',
    fuelLevel: 'full',
    condition: {
      exterior: 'good',
      interior: 'good',
      notes: ''
    },
    notes: '',
    damages: [],
    confirmed: false
  });
  
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [capturing, setCapturing] = useState(false);  const [signatureData, setSignatureData] = useState({
    employee: '',
    customer: ''
  });
  
  // Signature loading state
  const [signatureLoading, setSignatureLoading] = useState({
    employee: false,
    customer: false
  });
  
  // Refs for signature canvas
  const employeeSignatureRef = useRef(null);
  const customerSignatureRef = useRef(null);
  
  // Signature modal state
  const [signingMode, setSigningMode] = useState(null); // 'employee' or 'customer'
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  
  // Step tracking for multi-step form
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const [showAnimation, setShowAnimation] = useState(false);  
  const [stepErrors, setStepErrors] = useState({
    step1: null,
    step2: null,
    step3: null,
    step4: null
  });
    useEffect(() => {
    const fetchRentalData = async () => {
      try {
        setLoading(true);
        
        if (rentalId) {          // If we have a specific rental ID, fetch that rental
          const response = await rentals.getById(rentalId);
          setRental(response.data);
          
          // Fetch available vehicles for this car type
          console.log('Rental car data:', response.data.car);
          // Make sure we have a valid ID string
          const carId = response.data.car && response.data.car._id ? response.data.car._id : response.data.car;
          console.log('Using car ID:', carId);

          const carCategory = response.data.car.category;
          
          // For pickup, we need available vehicles
          if (formData.handoverType === 'pickup') {
            const vehiclesResponse = await cars.getVehicles(carId);
            console.log('Available vehicles:', vehiclesResponse.data);
            setAvailableVehicles(vehiclesResponse.data);
          } 
          // For return, we need the specific vehicle assigned to this rental
          else if (formData.handoverType === 'return' && response.data.vehicleInstanceId) {
            try {
              const sameVehicleCategorySelected = await cars.getVehiclesFromCategory(carCategory);
              console.log('Fetched vehicles from the same category:', sameVehicleCategorySelected.data);
              // search all the cars from the category for the selected vehicle
              const vehicle = sameVehicleCategorySelected.data.find(vehicle => vehicle._id === response.data.vehicleInstanceId);
              console.log('Fetched vehicle for return 2:', vehicle);
              setAvailableVehicles([vehicle]);
              // Auto-select the vehicle since there's only one option for return
              setSelectedVehicleId(response.data.vehicleInstanceId);
            } catch (vehicleErr) {
              console.error('Error fetching specific vehicle:', vehicleErr);
              // Fallback to all vehicles if specific vehicle can't be fetched
              const vehiclesResponse = await cars.getVehicles(carId);
              setAvailableVehicles(vehiclesResponse.data);
            }
          }        } else {
          // Otherwise, fetch all rentals based on handover type
          const response = await rentals.getAll();
          console.log('Rentals API response:', response);
          console.log('Response data structure:', response.data);
          console.log('Is response.data an array?', Array.isArray(response.data));
          
          // Ensure we have a valid array to work with
          const rentalsData = Array.isArray(response.data) ? response.data : 
                             (response.data && Array.isArray(response.data.rentals)) ? response.data.rentals : [];
          
          console.log('Final rentals data to use:', rentalsData);
          
          if (formData.handoverType === 'pickup') {
            // Filter rentals that are confirmed but don't have a handover yet
            const pendingRentalsList = rentalsData.filter(
              rental => rental.status === 'pending' && !rental.handoverCompleted
            );
            setPendingRentals(pendingRentalsList);
          } else {
            // For return, we need active rentals WITH assigned vehicles
            const activeRentalsList = rentalsData.filter(
              rental => (rental.status === 'active' || rental.status === 'confirmed') && 
                        rental.vehicleInstanceId && rental.vehicleInstanceId.length > 0
            );
            setPendingRentals(activeRentalsList);
          }
        }
        
        setLoading(false);
        // Trigger animations after loading is complete
        setTimeout(() => setShowAnimation(true), 300);
      } catch (err) {
        console.error('Error fetching rental data:', err);
        setError('Failed to load rental data. ' + (err.message || ''));
        setLoading(false);
      }
    };

    fetchRentalData();
  }, [rentalId, formData.handoverType]);

  // Admin detection and employee fetching
  useEffect(() => {
    const checkAdminAndFetchEmployees = async () => {
      try {
        // Check if user is admin
        const adminToken = localStorage.getItem('adminToken');
        const isAdminUser = !!adminToken;
        setIsAdmin(isAdminUser);
        
        // If admin, fetch employees
        if (isAdminUser) {
          const employeesResponse = await employeeService.getAllEmployees();
          setEmployees(employeesResponse.data);
          console.log('Fetched employees for admin:', employeesResponse.data);
        }
      } catch (err) {
        console.error('Error checking admin status or fetching employees:', err);
      }
    };

    checkAdminAndFetchEmployees();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
      
      // Special handling for handover type changes
      if (name === 'handoverType') {
        // Reset selection when changing handover type
        setSelectedRental(null);
        setFormData(prev => ({
          ...prev,
          rental: ''
        }));
        // Reset vehicle selection and available vehicles when changing handover type
        setSelectedVehicleId('');
        setAvailableVehicles([]);
        setCarModel(null);
          // Fetch different rentals based on handover type
        const fetchRentalsByType = async () => {
          try {
            setLoading(true);
            const response = await rentals.getAll();
            console.log('Fetching rentals by type - API response:', response);
            
            // Ensure we have a valid array to work with
            const rentalsData = Array.isArray(response.data) ? response.data : 
                               (response.data && Array.isArray(response.data.rentals)) ? response.data.rentals : [];
            
            if (value === 'pickup') {
              // For pickup, we need pending rentals
              const pendingRentalsList = rentalsData.filter(
                rental => rental.status === 'pending' && !rental.handoverCompleted
              );
              setPendingRentals(pendingRentalsList);
            } else if (value === 'return') {
              // For return, we need active/confirmed rentals with attached vehicles
              const activeRentalsList = rentalsData.filter(
                rental => (rental.status === 'active' || rental.status === 'confirmed') && 
                         rental.vehicleInstanceId && rental.vehicleInstanceId.length > 0
              );
              setPendingRentals(activeRentalsList);
            }
            
            setLoading(false);
          } catch (err) {
            console.error('Error fetching rentals by type:', err);
            setError('Failed to load rental data for ' + value + ' handover.');
            setLoading(false);
          }
        };
        
        fetchRentalsByType();
      }
    }
  };  const handleVehicleSelection = (e) => {
    console.log('Vehicle selected with ID:', e.target.value);
    setSelectedVehicleId(e.target.value);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setMediaFiles([...mediaFiles, ...files]);
  };

  const captureMedia = (type) => {
    setCapturing(type);
    
    // Check if it's a mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // On mobile, we can use more specific input capture methods
      if (type === 'photo') {
        // For photos on mobile, we can use the camera directly
        const input = fileInputRef.current;
        input.setAttribute('capture', 'environment'); // Use the back camera
        input.setAttribute('accept', 'image/*');
      } else if (type === 'video') {
        // For videos on mobile, use video capture
        const input = fileInputRef.current;
        input.setAttribute('capture', 'environment');
        input.setAttribute('accept', 'video/*');
      } else if (type === 'voice') {
        // For audio on mobile, use audio capture
        const input = fileInputRef.current;
        input.setAttribute('capture', '');
        input.setAttribute('accept', 'audio/*');
      }
    } else {
      // On desktop, just use the normal file picker without capture attribute
      const input = fileInputRef.current;
      input.removeAttribute('capture');
      
      if (type === 'photo') {
        input.setAttribute('accept', 'image/*');
      } else if (type === 'video') {
        input.setAttribute('accept', 'video/*');
      } else if (type === 'voice') {
        input.setAttribute('accept', 'audio/*');
      }
    }
    
    // Trigger the file input click
    fileInputRef.current.click();
  };

  const addDamage = () => {
    setFormData({
      ...formData,
      damages: [
        ...formData.damages,
        {
          location: '',
          description: '',
          photoUrl: '',
          severity: 'minor'
        }
      ]
    });
  };

  const updateDamage = (index, field, value) => {
    const updatedDamages = [...formData.damages];
    updatedDamages[index][field] = value;
    
    setFormData({
      ...formData,
      damages: updatedDamages
    });
  };

  const removeDamage = (index) => {
    const updatedDamages = formData.damages.filter((_, i) => i !== index);
    
    setFormData({
      ...formData,
      damages: updatedDamages
    });
  };
  
  // Signature handling functions
  const openSignatureModal = (type) => {
    setSigningMode(type);
    setShowSignatureModal(true);
  };
  
  const clearSignature = () => {
    if (signingMode === 'employee' && employeeSignatureRef.current) {
      employeeSignatureRef.current.clear();
    } else if (signingMode === 'customer' && customerSignatureRef.current) {
      customerSignatureRef.current.clear();
    }
  };
      const saveSignature = async () => {
    let signatureCanvas;
    
    if (signingMode === 'employee') {
      signatureCanvas = employeeSignatureRef.current;
    } else if (signingMode === 'customer') {
      signatureCanvas = customerSignatureRef.current;
    }
    
    if (signatureCanvas && !signatureCanvas.isEmpty()) {
      try {
        setSignatureLoading(prev => ({ ...prev, [signingMode]: true }));
        const dataURL = signatureCanvas.toDataURL('image/png');
        const response = await axios.post('https://rent-server.create-site.gr/api/signatures/upload-base64', {
          signature: dataURL
        });
        
        if (response.data.success && response.data.filePath) {
          const signatureUrl = `https://rent-server.create-site.gr${response.data.filePath}`;
          setSignatureData(prev => ({
            ...prev,
            [signingMode]: signatureUrl
          }));
          setShowSignatureModal(false);
        } else {
          setError('Failed to save signature: ' + (response.data.message || 'Server error or missing filePath'));
        }
      } catch (err) {
        console.error('Error saving signature:', err);
        setError('Error saving signature: ' + (err.message || 'Unknown error'));
      } finally {
        setSignatureLoading(prev => ({ ...prev, [signingMode]: false }));
      }
    }
  };
  
  const cancelSignature = () => {
    setShowSignatureModal(false);
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(Math.min(currentStep + 1, totalSteps));
      window.scrollTo(0, 0);
    }
  };
  const prevStep = () => {
    setCurrentStep(Math.max(currentStep - 1, 1));
    window.scrollTo(0, 0);
  };
  
  // Animation variants for elements
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.4 }
    }
  };  
  
 
  const validateStep = (step) => {
    switch(step) {
      case 1:
        // Step 1 validation (Basic Information)
        if (!formData.rental) {
          setStepErrors(prev => ({ ...prev, step1: 'Please select a rental' }));
          return false;
        }
        
        // Admin validation: require employee selection
        if (isAdmin && !selectedEmployeeId) {
          setStepErrors(prev => ({ ...prev, step1: 'Please select an employee for this handover' }));
          return false;
        }
          if (formData.handoverType === 'pickup' && !selectedVehicleId) {
          setStepErrors(prev => ({ ...prev, step1: 'Please select a vehicle for pickup' }));
          return false;
        }
        
        // Mileage validation only if provided and must be positive
        if (formData.mileage && formData.mileage <= 0) {
          setStepErrors(prev => ({ ...prev, step1: 'Please enter a valid mileage' }));
          return false;
        }
          // For return handovers, validate mileage is greater than current vehicle mileage only if provided
        if (formData.handoverType === 'return' && formData.mileage && selectedVehicleId && availableVehicles.length > 0) {
          const selectedVehicle = availableVehicles.find(v => v._id === selectedVehicleId);
          if (selectedVehicle && selectedVehicle.currentMileage) {
            const currentVehicleMileage = selectedVehicle.currentMileage;
            if (Number(formData.mileage) < currentVehicleMileage) {
              setStepErrors(prev => ({ 
                ...prev, 
                step1: `Return mileage must be at least ${currentVehicleMileage} km (current vehicle mileage)` 
              }));
              return false;
            }
          }
        }
        
        // Clear error if validation passes
        setStepErrors(prev => ({ ...prev, step1: null }));
        return true;
        
      case 2:
        // Step 2 validation (Vehicle Condition)
        if (formData.damages.length > 0) {
          // Validate each damage has at least location and description
          const invalidDamages = formData.damages.some(
            damage => !damage.location || !damage.description
          );
          
          if (invalidDamages) {
            setStepErrors(prev => ({ 
              ...prev, 
              step2: 'Please complete all damage information (location and description are required)' 
            }));
            return false;
          }
        }
        
        // Clear error if validation passes
        setStepErrors(prev => ({ ...prev, step2: null }));
        return true;
        
      case 3:
        // Step 3 validation (Documentation)
        // This step is optional, so we'll just clear any errors
        setStepErrors(prev => ({ ...prev, step3: null }));
        return true;
          case 4:
        // Step 4 validation (Signatures)
        // Skip signature validation for admin-created handovers
        if (isAdmin) {
          setStepErrors(prev => ({ ...prev, step4: null }));
          return true;
        }
        
        if (!signatureData.employee) {
          setStepErrors(prev => ({ ...prev, step4: 'Employee signature is required' }));
          return false;
        }
        
        if (!signatureData.customer) {
          setStepErrors(prev => ({ ...prev, step4: 'Customer signature is required' }));
          return false;
        }
        
        // Clear error if validation passes
        setStepErrors(prev => ({ ...prev, step4: null }));
        return true;
        
      default:
        return true;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all steps before submitting
    for (let step = 1; step <= totalSteps; step++) {
      if (!validateStep(step)) {
        setCurrentStep(step);
        return;
      }
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Get the rental data if we have a selected rental
      let rentalData = selectedRental || rental;
      if (!rentalData && formData.rental) {
        const response = await rentals.getById(formData.rental);
        rentalData = response.data;
      }

      // For return handovers, use the vehicle from the rental if not explicitly selected
      let vehicleInstanceIdToUse = selectedVehicleId;
      if (formData.handoverType === 'return' && !vehicleInstanceIdToUse && rentalData) {
        vehicleInstanceIdToUse = rentalData.vehicleInstanceId;
      }

      console.log('Using vehicleInstanceId for submission:', vehicleInstanceIdToUse);
      
      // For pickup handovers, we need to update the rental with the selected vehicle first
      if (formData.handoverType === 'pickup') {
        console.log('Updating rental with selected vehicle:', vehicleInstanceIdToUse);
        try {
          await rentals.update(formData.rental, { vehicleInstanceId: vehicleInstanceIdToUse });
        } catch (updateErr) {
          console.error('Error updating rental with vehicle:', updateErr);
          setError('Failed to assign vehicle to rental: ' + (updateErr.message || 'Unknown error'));
          setLoading(false);
          return;
        }
      }      // Prepare the handover data
      const handoverData = {
        rental: formData.rental,
        handoverType: formData.handoverType,
        vehicleInstanceId: vehicleInstanceIdToUse,
        mileage: formData.mileage,
        fuelLevel: formData.fuelLevel,
        condition: {
          exterior: formData.condition.exterior,
          interior: formData.condition.interior,
          notes: formData.condition.notes
        },
        damages: formData.damages,
        notes: formData.notes,
        signatures: {
          employee: signatureData.employee,
          customer: signatureData.customer
        },
        customer: rentalData ? {
          name: `${rentalData.customer.firstName} ${rentalData.customer.lastName}`,
          email: rentalData.customer.email,
          phoneNumber: rentalData.customer.phoneNumber || rentalData.customer.phone
        } : null,
        // Handle employee assignment based on user type
        employeeId: isAdmin ? selectedEmployeeId : localStorage.getItem('employeeId'),
        // Add admin-specific fields
        ...(isAdmin && {
          createdBy: 'admin',
          assignedEmployee: selectedEmployeeId
        })
      };

      console.log('Submitting handover data:', handoverData);
      
      // Create the handover
      const response = await handoverService.createHandover(handoverData);
      console.log('Handover created successfully:', response.data);
      const handoverId = response.data._id;

      // Upload any media files using the correct endpoint
      if (mediaFiles.length > 0) {
        try {
          console.log(`Starting upload of ${mediaFiles.length} media files to handover ${handoverId}`);
          
          // Group files by type
          const imageFiles = mediaFiles.filter(file => file.type.startsWith('image/'));
          const videoFiles = mediaFiles.filter(file => file.type.startsWith('video/'));
          const audioFiles = mediaFiles.filter(file => file.type.startsWith('audio/'));
          
          console.log('Files grouped by type:', {
            images: imageFiles.length,
            videos: videoFiles.length,
            audio: audioFiles.length
          });
          
          // Upload each file individually using the correct service method
          let uploadCount = 0;
          
          // Upload images
          for (const file of imageFiles) {
            const imageFormData = new FormData();
            imageFormData.append('file', file);
            imageFormData.append('documentType', 'photo');
            imageFormData.append('caption', `${file.name} - ${moment().format('DD/MM/YYYY HH:mm')}`);
            
            console.log(`Uploading photo ${uploadCount + 1}/${mediaFiles.length}: ${file.name}`);
            await handoverService.addDocumentation(handoverId, imageFormData);
            uploadCount++;
            
            // Add small delay between uploads
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          // Upload videos
          for (const file of videoFiles) {
            const videoFormData = new FormData();
            videoFormData.append('file', file);
            videoFormData.append('documentType', 'video');
            videoFormData.append('caption', `${file.name} - ${moment().format('DD/MM/YYYY HH:mm')}`);
            
            console.log(`Uploading video ${uploadCount + 1}/${mediaFiles.length}: ${file.name}`);
            await handoverService.addDocumentation(handoverId, videoFormData);
            uploadCount++;
            
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          // Upload audio files
          for (const file of audioFiles) {
            const audioFormData = new FormData();
            audioFormData.append('file', file);
            audioFormData.append('documentType', 'voiceNote');
            audioFormData.append('caption', `${file.name} - ${moment().format('DD/MM/YYYY HH:mm')}`);
            
            console.log(`Uploading audio ${uploadCount + 1}/${mediaFiles.length}: ${file.name}`);
            await handoverService.addDocumentation(handoverId, audioFormData);
            uploadCount++;
            
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          console.log(`Successfully uploaded ${uploadCount} files`);
          
        } catch (uploadError) {
          console.error('Error uploading files:', uploadError);
          setError('Some files could not be uploaded: ' + uploadError.message);
        }
      }

      // Update the rental status based on handover type
      if (formData.handoverType === 'pickup') {
        await rentals.updateStatus(formData.rental, 'confirmed');
        setShowSuccessMessage('pickup');
      } else if (formData.handoverType === 'return') {
        await rentals.updateStatus(formData.rental, 'completed');
        
        try {
          const handoversResponse = await handoverService.getHandoversByRental(formData.rental);
          const pickupHandover = handoversResponse.data.find(h => h.handoverType === 'pickup');
          if (pickupHandover) {
            await handoverService.updateHandover(pickupHandover._id, { status: 'completed' });
          }
        } catch (err) {
          console.error('Error updating pickup handover status:', err);
        }
      }
      
      setLoading(false);
      
      if (isModal) {
        onClose();
      }
      
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      }, 500);
      
    } catch (err) {
      console.error('Error submitting handover:', err);
      setError('Failed to submit handover: ' + (err.message || 'Unknown error'));
      setLoading(false);
    }
  };
  if (loading && !rental) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <motion.div 
            animate={{ 
              rotate: 360,
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              rotate: { duration: 1.5, repeat: Infinity, ease: "linear" },
              scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
            }}
            className="w-20 h-20 mx-auto mb-4 relative"
          >
            <div className="absolute inset-0 rounded-full border-t-4 border-b-4 border-blue-600"></div>
            <div className="absolute inset-2 rounded-full border-r-4 border-l-4 border-blue-400 opacity-75"></div>
          </motion.div>
          <p className="text-blue-800 text-lg font-medium">
            Loading...
          </p>
          <p className="text-blue-600 text-sm mt-2">
            Fetching rental data
          </p>
        </motion.div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 ">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-yellow-50 border-l-4 border-red-500 p-6 rounded-lg shadow-md my-4"
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="h-6 w-6 text-yellow-500" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-yellow-800">
                Error
              </h3>
              <div className="mt-2">
                <p className="text-yellow-700">{error}</p>
              </div>
              <div className="mt-4">
                <motion.button
                  onClick={() => navigate(-1)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded flex items-center"
                >
                  <FaArrowLeft className="mr-2" />
                  Back
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }  
  const renderStepError = (step) => {
    const errorKey = `step${step}`;
    const error = stepErrors[errorKey];
    
    if (!error) return null;
    
    return (
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-6 p-3 bg-yellow-50 border-l-4 border-red-500 rounded-r-lg"
      >
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <FaExclamationTriangle className="h-5 w-5 text-yellow-500" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">{error}</p>
          </div>
        </div>
      </motion.div>
    );
  };

  // Multi-step form rendering
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <motion.h2 
              variants={itemVariants}
              className="text-xl font-semibold mb-6 text-gray-800 flex items-center"
            >
              <FaClipboard className="mr-2 text-blue-600" />
              Basic Information
            </motion.h2>
            
            {renderStepError(1)}
            
            {!rentalId && (
              <motion.div variants={itemVariants} className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-100">
          <label className="text-gray-700 font-medium mb-2 inline-flex items-center">
            <FaClipboardList className="mr-2 text-blue-600" />
            {formData.handoverType === 'pickup' 
              ? 'Select Pending Rental' 
              : 'Select Confirmed Rental for Return'}
          </label>
          
          {rentalsByHandoverType.length > 0 ? (
            <select
              value={selectedRental ? selectedRental._id : ''}
              onChange={(e) => {
                const selected = rentalsByHandoverType.find(r => r._id === e.target.value);
                setSelectedRental(selected);
                setFormData(prev => ({
            ...prev,
            rental: e.target.value
                }));
                
                // When a rental is selected, fetch available vehicles for this car type
                if (selected) {
            const fetchVehicles = async () => {
              try {
                console.log('Selected rental car:', selected.car);
                // Make sure we have a valid ID string
                const carId = selected.car && selected.car._id ? selected.car._id : selected.car;
                console.log('Using car ID:', carId);
                
                if (formData.handoverType === 'pickup') {
                  // For pickup, we need available vehicles from the same category
                  const carCategory = selected.car.category;
                  setCarModel(selected.car.make + ' ' + selected.car.model);
                  const vehiclesResponse = await cars.getVehiclesFromCategory(carCategory);
                  console.log('Fetched available vehicles for pickup:', vehiclesResponse.data);
                  // Filter for available vehicles not already assigned to rentals
                  const availableVehiclesFiltered = vehiclesResponse.data.filter(
              vehicle => vehicle.status === 'available' || vehicle.status === 'maintenance'
                  );
                  setAvailableVehicles(availableVehiclesFiltered);
                  setSelectedVehicleId(''); // Reset selection for pickup
                } else if (formData.handoverType === 'return') {
                  // For return, we need the specific vehicle assigned to this rental
                  if (selected.vehicleInstanceId) {
              // Try to get the specific vehicle from the category first
              const carCategory = selected.car.category;
              try {
                const sameVehicleCategorySelected = await cars.getVehiclesFromCategory(carCategory);
                console.log('Fetched vehicles from the same category:', sameVehicleCategorySelected.data);
                
                // Find the exact vehicle from the category that matches the vehicleInstanceId
                const vehicle = sameVehicleCategorySelected.data.find(
                  v => v._id === selected.vehicleInstanceId
                );
                
                if (vehicle) {
                  console.log('Fetched specific vehicle for return:', vehicle);
                  setAvailableVehicles([vehicle]);
                  setSelectedVehicleId(selected.vehicleInstanceId);
                  
                  // Set the mileage to current vehicle mileage as a starting point
                  if (vehicle.currentMileage) {
                    setFormData(prev => ({
                ...prev,
                mileage: vehicle.currentMileage
                    }));
                  }
                } else {
                  throw new Error('Vehicle not found in category');
                }
              } catch (categoryErr) {
                console.error('Error finding vehicle in category, fetching directly:', categoryErr);
                
                // Fallback: try to fetch the specific vehicle directly
                try {
                  const vehicleResponse = await cars.getVehicleById(selected.vehicleInstanceId);
                  console.log('Fetched vehicle directly:', vehicleResponse.data);
                  setAvailableVehicles([vehicleResponse.data]);
                  setSelectedVehicleId(selected.vehicleInstanceId);
                  
                  // Set the mileage to current vehicle mileage as a starting point
                  if (vehicleResponse.data.currentMileage) {
                    setFormData(prev => ({
                ...prev,
                mileage: vehicleResponse.data.currentMileage
                    }));
                  }
                } catch (directErr) {
                  console.error('Error fetching vehicle directly:', directErr);
                  // Just log the error but don't set an error message for the user
                  console.error('Failed to fetch the vehicle for this rental');
                  setAvailableVehicles([]);
                }
              }
                  }
                }
              } catch (err) {
                console.error('Error fetching vehicles:', err);
                setError('Failed to fetch available vehicles: ' + (err.message || ''));
              }
            };
            fetchVehicles();
                }
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            >
              <option value="">
                {formData.handoverType === 'pickup' 
            ? 'Select a pending rental' 
            : 'Select a confirmed rental to return'}
              </option>
              {rentalsByHandoverType.map(rental => (
                <option key={rental._id} value={rental._id}>
            {rental.carName} - {rental.customer.firstName} {rental.customer.lastName} - {moment(rental.pickupDateTime).format('MMM DD, YYYY')}
                </option>
              ))}
            </select>
          ) : (
            <div className="p-3 border border-yellow-200 rounded-lg bg-yellow-50 text-yellow-700">
              {formData.handoverType === 'pickup' 
                ? 'No pending rentals are available for pickup at this time.' 
                : 'No rentals are available for return. This may be because no vehicles have been assigned to confirmed rentals.'}
            </div>
          )}
              </motion.div>
            )}
            
            <motion.div variants={itemVariants} className="mb-6">
              <label className="text-gray-700 font-medium mb-2 inline-flex items-center">Handover Type</label>
              <div className="flex gap-4">
          <motion.div 
            onClick={() => handleInputChange({ target: { name: 'handoverType', value: 'pickup' } })}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 flex items-center ${
              formData.handoverType === 'pickup' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
              formData.handoverType === 'pickup' ? 'border-blue-500' : 'border-gray-400'
            }`}>
              {formData.handoverType === 'pickup' && <div className="w-3 h-3 bg-blue-500 rounded-full" />}
            </div>                  <div>
              <div className="font-medium">Pickup</div>
              <div className="text-sm text-gray-500">Vehicle handover to customer at the start of rental</div>
            </div>
          </motion.div>
          
          <motion.div 
            onClick={() => handleInputChange({ target: { name: 'handoverType', value: 'return' } })}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 flex items-center ${
              formData.handoverType === 'return' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
              formData.handoverType === 'return' ? 'border-blue-500' : 'border-gray-400'
            }`}>
              {formData.handoverType === 'return' && <div className="w-3 h-3 bg-blue-500 rounded-full" />}
            </div>
            <div>
              <div className="font-medium">Return</div>
              <div className="text-sm text-gray-500">Vehicle return from customer at the end of rental</div>
            </div>
          </motion.div>              </div>
            </motion.div>
            
            {/* Employee Selection for Admin Users */}
            {isAdmin && (
              <motion.div variants={itemVariants} className="mb-6">
                <label className="text-gray-700 font-medium mb-2 inline-flex items-center">
                  <FaUser className="mr-2 text-purple-600" />
                  Assign Employee
                </label>
                <select
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select an employee</option>
                  {employees.map((employee) => (
                    <option key={employee._id} value={employee._id}>
                      {employee.firstName} {employee.lastName} - {employee.email}
                    </option>
                  ))}
                </select>
                {isAdmin && !selectedEmployeeId && (
                  <p className="text-yellow-500 text-sm mt-1">Please select an employee for this handover</p>
                )}
              </motion.div>
            )}
            
            {formData.handoverType === 'pickup' ? (
              <motion.div 
          variants={itemVariants}
          className="mb-6 bg-white p-5 border border-gray-200 rounded-lg shadow-sm"
              >
          <label className="text-gray-700 font-medium mb-2 inline-flex items-center">
            <FaCar className="mr-2 text-blue-600" />
            Select Vehicle
          </label>
          {availableVehicles.length > 0 ? (
            <>
              <select
                value={selectedVehicleId}
                onChange={(e) => {
            handleVehicleSelection(e);
            
            // Set the mileage to the current vehicle mileage as a starting point
            const selectedVehicle = availableVehicles.find(v => v._id === e.target.value);
            if (selectedVehicle && selectedVehicle.currentMileage) {
              setFormData(prev => ({
                ...prev,
                mileage: selectedVehicle.currentMileage
              }));
            }
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              >
                <option value="">Select Vehicle</option>                  
                {availableVehicles.map(vehicle => (
            <option className='capitalize' key={vehicle._id} value={vehicle._id}>
              Plate: ({vehicle.licensePlate}) - Mileage: {vehicle.currentMileage}
            </option>
                ))}
              </select>
              
              {/* Display vehicle details outside the dropdown for mobile/tablet */}
              {selectedVehicleId && (
                <div className="mt-4">
            {availableVehicles.find(v => v._id === selectedVehicleId) && (
              <div className="p-4 border border-blue-100 rounded-lg bg-blue-50">
                <h4 className="font-medium text-blue-700 mb-2">Vehicle Details:</h4>
                
                <div className="grid grid-cols-1 gap-2 text-sm text-blue-800">
                  <div className="flex justify-between">
              <span className="font-medium">License Plate:</span>
              <span>{availableVehicles.find(v => v._id === selectedVehicleId)?.licensePlate}</span>
                  </div>
                  
                  <div className="flex justify-between">
              <span className="font-medium">Current Mileage:</span>
              <span>{availableVehicles.find(v => v._id === selectedVehicleId)?.currentMileage} km</span>
                  </div>
                  
                  <div className="flex justify-between">
              <span className="font-medium">Insurance:</span>
              <span>
                {availableVehicles.find(v => v._id === selectedVehicleId)?.insurance?.type === 'mixed' 
                  ? `Mixed, at $${availableVehicles.find(v => v._id === selectedVehicleId)?.insurance?.coverageAmount}` 
                  : `Type: ${availableVehicles.find(v => v._id === selectedVehicleId)?.insurance?.type || 'N/A'}`}
              </span>
                  </div>
                  
                  <div className="flex justify-between">
              <span className="font-medium">ID:</span>
              <span className="text-xs">{selectedVehicleId}</span>
                  </div>
                </div>
                
                {availableVehicles.find(v => v._id === selectedVehicleId)?.specialNotes && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
              <div className="flex items-start">
                <FaInfoCircle className="text-blue-500 mt-1 mr-2" />
                <div>
                  <span className="font-medium text-blue-700">Special Notes: </span>
                  <span className="text-blue-800">
                    {availableVehicles.find(v => v._id === selectedVehicleId)?.specialNotes}
                  </span>
                </div>
              </div>
                  </div>
                )}
              </div>
            )}
                </div>
              )}
            </>
          ) : (
            <div className="p-3 border border-yellow-200 rounded-lg bg-yellow-50 text-yellow-700">
              Please Choose a Rental.
            </div>
          )}
              </motion.div>
            ) : (
              <motion.div 
          variants={itemVariants}
          className="mb-6 bg-white p-5 border border-gray-200 rounded-lg shadow-sm"
              >
          <label className="text-gray-700 font-medium mb-2 inline-flex items-center">
            <FaCar className="mr-2 text-blue-600" />
            Vehicle for Return
          </label>
          {console.log('Available vehicles for return:', availableVehicles)}
          {availableVehicles.length > 0 ? (
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h4 className="font-medium text-gray-700 mb-3">Vehicle Details:</h4>
              
              <div className="grid grid-cols-1 gap-3">
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
            <span className="font-medium text-gray-600">License Plate:</span>
            <span className="text-gray-800">{availableVehicles[0].licensePlate}</span>
                </div>
                
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
            <span className="font-medium text-gray-600">VIN:</span>
            <span className="text-gray-800">{availableVehicles[0].vin}</span>
                </div>
                
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
            <span className="font-medium text-gray-600">Current Mileage:</span>
            <span className="text-gray-800">{availableVehicles[0].currentMileage} km</span>
                </div>
                
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
            <span className="font-medium text-gray-600">Status:</span>
            <span className="text-gray-800 capitalize">{availableVehicles[0].status}</span>
                </div>
                
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
            <span className="font-medium text-gray-600">Condition:</span>
            <span className="text-gray-800 capitalize">{availableVehicles[0].condition}</span>
                </div>
                
                <div className="flex justify-between items-center">
            <span className="font-medium text-gray-600">Insurance:</span>
            <span className="text-gray-800">
              {availableVehicles[0].insurance && availableVehicles[0].insurance.type === 'mixed' 
                ? `Mixed, at $${availableVehicles[0].insurance.coverageAmount}` 
                : `Type: ${availableVehicles[0].insurance && availableVehicles[0].insurance.type || 'N/A'}`}
            </span>
                </div>
              </div>
              
              {availableVehicles[0].specialNotes && (
                <div className="mt-4 p-3 border border-blue-100 rounded-lg bg-blue-50">
            <div className="flex items-start">
              <FaInfoCircle className="text-blue-500 mt-1 mr-2 flex-shrink-0" />
              <div>
                <span className="font-medium text-blue-700">Special Notes: </span>
                <span className="text-blue-800">{availableVehicles[0].specialNotes}</span>
              </div>
            </div>
                </div>
              )}
            </div>
          ) : selectedRental ? (
            <div className="p-3 border border-red-200 rounded-lg bg-yellow-50 text-yellow-700">
              No vehicle has been assigned to this rental. A vehicle assignment is required for return processing.
            </div>
          ) : (
            <div className="p-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 italic">
              Vehicle information will display when you select a rental
            </div>
          )}
          <input type="hidden" value={selectedVehicleId} />
              </motion.div>
            )}
            
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div variants={itemVariants} className="mb-4">                
          <label className="text-gray-700 font-medium mb-2 inline-flex items-center">
            <FaTachometerAlt className="mr-2 text-blue-600" />
            Mileage
          </label>
          <div className="relative">
            <input
              type="number"
              name="mileage"
              value={formData.mileage}
              onChange={(e) => {
                const newMileage = Number(e.target.value);
                
                // Get the current mileage of the selected vehicle
                let currentVehicleMileage = 0;
                if (selectedVehicleId && availableVehicles.length > 0) {
            const selectedVehicle = availableVehicles.find(v => v._id === selectedVehicleId);
            if (selectedVehicle) {
              currentVehicleMileage = selectedVehicle.currentMileage || 0;
            }
                }
                  // For return, mileage should be higher than current mileage only if provided
                if (formData.handoverType === 'return' && newMileage > 0 && newMileage < currentVehicleMileage) {
            setStepErrors(prev => ({ 
              ...prev, 
              step1: `Return mileage must be at least ${currentVehicleMileage} km (current vehicle mileage)` 
            }));
                } else if (newMileage < 0) {
            setStepErrors(prev => ({ 
              ...prev, 
              step1: 'Mileage cannot be negative' 
            }));
                } else {
            // Clear error if validation passes
            if (stepErrors.step1 && stepErrors.step1.includes('mileage')) {
              setStepErrors(prev => ({ ...prev, step1: null }));
            }
                }
                
                // Update the form data regardless (we'll validate before submission)
                handleInputChange({ target: { name: 'mileage', value: e.target.value } });
              }}              className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              placeholder="Enter current mileage (optional)"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <span className="text-gray-500">km</span>
            </div>
          </div>                {formData.handoverType === 'return' && (
            <div className="mt-1 space-y-1">              <p className="text-sm text-blue-600">
                <FaInfoCircle className="inline mr-1" /> 
                {selectedVehicleId && availableVehicles.length > 0 ? 
            `If providing mileage, it must be higher than ${availableVehicles.find(v => v._id === selectedVehicleId)?.currentMileage} km (current vehicle mileage).` :
            'If providing mileage, it should be higher than at pickup.'
                }
              </p>
              <p className="text-sm text-green-600">
                <FaCheckCircle className="inline mr-1" /> 
                If provided, this mileage will be saved as the vehicle's current mileage after return.
              </p>
            </div>
          )}
              </motion.div>
              
              <motion.div variants={itemVariants} className="mb-4">
          <label className="text-gray-700 font-medium mb-2 inline-flex items-center">
            <FaGasPump className="mr-2 text-blue-600" />
            Fuel Level
          </label>
          <select
            name="fuelLevel"
            value={formData.fuelLevel}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          >
            <option value="">Select Fuel Level</option>
            <option value="empty">Empty</option>
            <option value="1/8">1/8</option>
            <option value="2/8">2/8</option>
            <option value="3/8">3/8</option>
            <option value="4/8">4/8</option>
            <option value="5/8">5/8</option>
            <option value="6/8">6/8</option>
            <option value="7/8">7/8</option>
            <option value="full">Full</option>
          </select>
              </motion.div>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div>
            <h2 className="text-lg font-semibold mb-4">Vehicle Condition</h2>
            
            {renderStepError(2)}
            
            <div className="mb-4">
              <label className="inline-flex text-gray-700 mb-2">Exterior Condition</label>
              <select
                name="condition.exterior"
                value={formData.condition.exterior}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>
              <div className="mb-4">
              <label className="inline-flex text-gray-700 mb-2">Interior Condition</label>
              <select
                name="condition.interior"
                value={formData.condition.interior}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div><div className="mb-4">
              <label className="inline-flex text-gray-700 mb-2">Condition Notes</label>
              <textarea
                name="condition.notes"
                value={formData.condition.notes}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded"
                rows="3"
              ></textarea>
            </div>
              <h3 className="font-semibold text-gray-700 mb-2">
              {formData.handoverType === 'pickup' ? 'Pre-existing Damages' : 'New Damages'}
            </h3>
            {formData.handoverType === 'return' && (
              <p className="text-sm text-gray-600 mb-4">
                Document any new damages that occurred during the rental period. This information may affect deposit refunds.
              </p>
            )}
            {formData.damages.map((damage, index) => (
              <div key={index} className="mb-4 p-3 border border-gray-300 rounded">
                <div className="mb-2">
                  <label className="inline-flex text-gray-700 mb-1">Damage Location</label>
                  <input
                    type="text"
                    value={damage.location}
                    onChange={(e) => updateDamage(index, 'location', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                </div>
                  <div className="mb-2">
                  <label className="inline-flex text-gray-700 mb-1">Damage Description</label>
                  <textarea
                    value={damage.description}
                    onChange={(e) => updateDamage(index, 'description', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                    rows="2"
                  ></textarea>
                </div>
                  <div className="mb-2">
                  <label className="inline-flex text-gray-700 mb-1">Damage Severity</label>
                  <select
                    value={damage.severity}
                    onChange={(e) => updateDamage(index, 'severity', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  >
                    <option value="minor">Minor</option>
                    <option value="moderate">Moderate</option>
                    <option value="severe">Severe</option>
                  </select>
                </div>
                  <button
                  type="button"
                  onClick={() => removeDamage(index)}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white py-1 px-3 rounded text-sm"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addDamage}
              className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded"
            >
              Add Damage
            </button>
          </div>
        );
               
      case 3:
        return (
          <div>
            <motion.h2 
              variants={itemVariants}
              className="text-xl font-semibold mb-6 text-gray-800 flex items-center"
            >
              <FaImage className="mr-2 text-blue-600" />
              Documentation
            </motion.h2>
            
            {renderStepError(3)}
            
            <motion.div 
              variants={itemVariants}
              className="mb-6 p-5 bg-indigo-50 rounded-lg border border-indigo-100"
            >
              <div className="flex flex-col">
                <h3 className="font-semibold text-indigo-800 mb-3 flex items-center">
                  <FaFileUpload className="mr-2 text-indigo-600" />
                  Media Upload
                </h3>
                
                <p className="text-indigo-700 mb-4 text-sm">
                  Upload photos, videos, or audio recordings of the vehicle condition.
                </p>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  accept="image/*,video/*,audio/*"
                  multiple
                />
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  <motion.button
                    type="button"
                    onClick={() => captureMedia('photo')}
                    whileHover={{ scale: 1.05, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
                    whileTap={{ scale: 0.95 }}
                    className="flex flex-col items-center justify-center bg-white border border-indigo-200 p-4 rounded-lg text-indigo-700 hover:bg-indigo-50 transition-all duration-200"
                  >
                    <FaImage className="text-3xl mb-2 text-indigo-500" />
                    <span className="text-sm font-medium">Capture Photo</span>
                  </motion.button>
                  
                  <motion.button
                    type="button"
                    onClick={() => captureMedia('video')}
                    whileHover={{ scale: 1.05, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
                    whileTap={{ scale: 0.95 }}
                    className="flex flex-col items-center justify-center bg-white border border-purple-200 p-4 rounded-lg text-purple-700 hover:bg-purple-50 transition-all duration-200"
                  >
                    <FaVideo className="text-3xl mb-2 text-purple-500" />
                    <span className="text-sm font-medium">Capture Video</span>
                  </motion.button>
                  
                  <motion.button
                    type="button"
                    onClick={() => captureMedia('voice')}
                    whileHover={{ scale: 1.05, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
                    whileTap={{ scale: 0.95 }}
                    className="flex flex-col items-center justify-center bg-white border border-blue-200 p-4 rounded-lg text-blue-700 hover:bg-blue-50 transition-all duration-200"
                  >
                    <FaMicrophone className="text-3xl mb-2 text-blue-500" />
                    <span className="text-sm font-medium">Record Voice</span>
                  </motion.button>
                </div>
              </div>
              
              <AnimatePresence>
                {mediaFiles.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-6"
                  >
                    <h3 className="font-semibold text-indigo-800 mb-2 flex items-center">
                      <FaClipboardList className="mr-2 text-indigo-600" />
                      Uploaded Files ({mediaFiles.length})
                    </h3>
                    
                    <div className="bg-white p-4 rounded-lg border border-indigo-100 max-h-56 overflow-y-auto">
                      {mediaFiles.map((file, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                          className="flex items-center justify-between py-2 px-3 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center">
                            <div className="text-indigo-500 mr-3">
                              {file.type.startsWith('image/') ? <FaImage/> : 
                               file.type.startsWith('video/') ? <FaVideo/> : <FaMicrophone/>}
                            </div>
                            <div className="text-sm text-gray-700">{file.name}</div>
                          </div>
                          <div className="flex items-center">
                            <div className="text-xs text-gray-500 mr-3">
                              {(file.size / 1024).toFixed(1)} KB
                            </div>
                            <button 
                              onClick={() => {
                                setMediaFiles(mediaFiles.filter((_, i) => i !== index));
                              }}
                              className="text-yellow-500 hover:text-yellow-700"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
            
            <motion.div variants={itemVariants} className="mb-6">
              <label className="text-gray-700 font-medium mb-2 inline-flex items-center">
                <FaClipboardList className="mr-2 text-blue-600" />
                Additional Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                rows="4"
                placeholder="Enter any additional details or notes about the handover"
              ></textarea>
            </motion.div>
          </div>
        );      case 4:
        return (
          <div>
            <motion.h2 
              variants={itemVariants}
              className="text-xl font-semibold mb-6 text-gray-800 flex items-center"
            >
              <FaPen className="mr-2 text-blue-600" />
              Signatures
            </motion.h2>
            
            {renderStepError(4)}
            
            {/* Admin notification about signatures */}
            {isAdmin && (
              <motion.div 
                variants={itemVariants}
                className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg"
              >
                <div className="flex items-center">
                  <FaInfoCircle className="text-blue-600 mr-2" />
                  <span className="text-blue-800 font-medium">Admin Note:</span>
                </div>
                <p className="text-blue-700 mt-1">
                  As an administrator, signatures are not required for handovers you create. 
                  You can proceed directly to submit the handover.
                </p>
              </motion.div>
            )}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <motion.div 
                variants={itemVariants}
                className="bg-white p-5 border border-gray-200 rounded-lg shadow-sm"
              >
                <label className="text-gray-700 font-medium mb-3 inline-flex">
                  Employee Signature
                </label>
                <div 
                  onClick={() => openSignatureModal('employee')}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 flex flex-col items-center justify-center mb-3 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  {signatureData.employee ? (
                    <div className="w-full">
                      <img 
                        src={signatureData.employee} 
                        alt="Employee Signature" 
                        className="max-h-32 mx-auto" 
                      />
                    </div>
                  ) : (
                    <>
                      <FaPen className="text-gray-400 text-4xl mb-3" />
                      <p className="text-gray-500 text-sm text-center mb-2">
                        Click here to sign
                      </p>
                    </>
                  )}
                </div>
                {signatureData.employee && (
                  <div className="flex justify-between mb-3">
                    <button
                      type="button"
                      onClick={() => openSignatureModal('employee')}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                    >
                      <FaPen className="mr-1" /> Edit signature
                    </button>
                    <button
                      type="button"
                      onClick={() => setSignatureData(prev => ({ ...prev, employee: '' }))}
                      className="text-yellow-600 hover:text-yellow-800 text-sm flex items-center"
                    >
                      <FaTrash className="mr-1" /> Clear
                    </button>
                  </div>
                )}
              </motion.div>
              
              <motion.div 
                variants={itemVariants}
                className="bg-white p-5 border border-gray-200 rounded-lg shadow-sm"
              >
                <label className="text-gray-700 font-medium mb-3 inline-flex">
                  Customer Signature
                </label>
                <div 
                  onClick={() => openSignatureModal('customer')}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 flex flex-col items-center justify-center mb-3 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  {signatureData.customer ? (
                    <div className="w-full">
                      <img 
                        src={signatureData.customer} 
                        alt="Customer Signature" 
                        className="max-h-32 mx-auto" 
                      />
                    </div>
                  ) : (
                    <>
                      <FaPen className="text-gray-400 text-4xl mb-3" />
                      <p className="text-gray-500 text-sm text-center mb-2">
                        Click here to sign
                      </p>
                    </>
                  )}
                </div>
                {signatureData.customer && (
                  <div className="flex justify-between mb-3">
                    <button
                      type="button"
                      onClick={() => openSignatureModal('customer')}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                    >
                      <FaPen className="mr-1" /> Edit signature
                    </button>
                    <button
                      type="button"
                      onClick={() => setSignatureData(prev => ({ ...prev, customer: '' }))}
                      className="text-yellow-600 hover:text-yellow-800 text-sm flex items-center"
                    >
                      <FaTrash className="mr-1" /> Clear
                    </button>
                  </div>
                )}
              </motion.div>
            </div>
            
            <motion.div
              variants={itemVariants}
              className="p-5 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg mb-6"
            >
              <h3 className="font-semibold text-yellow-800 mb-3 flex items-center">
                <FaExclamationTriangle className="mr-2 text-yellow-600" />
                Confirmation
              </h3>
              <p className="text-yellow-800 text-sm">
                Please confirm all information is correct
              </p>
              
              <motion.div
                className="mt-4 flex items-center"
                whileHover={{ scale: 1.01 }}
              >
                <input
                  type="checkbox"
                  id="confirmCheckbox"
                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 mr-3"
                />
                <label htmlFor="confirmCheckbox" className="text-gray-700">
                  I confirm all information is accurate
                </label>
              </motion.div>
            </motion.div>
            
            <motion.div
              variants={itemVariants}
              className="p-5 bg-green-50 border border-green-100 rounded-lg"
            >
              <div className="flex items-center mb-4">
                <div className="bg-green-100 p-2 rounded-lg mr-4">
                  <FaCheckCircle className="text-green-600 text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-green-800">
                    Ready to Submit
                  </h3>
                  <p className="text-green-700 text-sm">
                    Review all information before submitting
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        );
      default:  
        return null;}
  };
  return (
    <div className={`${isModal ? 'text-gray-500' : 'min-h-screen bg-gray-50 py-8'}`}>
      <div className={`container mx-auto ${isModal ? 'px-2' : 'px-4'}`}>
        <motion.div
          variants={containerVariants}
          className={`${isModal ? 'mx-auto' : 'max-w-4xl mx-auto'}`}
        >
          <motion.div 
            variants={itemVariants}
            className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-lg shadow-lg p-6 mb-6 text-white"
          >
            <h1 className="text-2xl md:text-3xl font-bold mb-2">
              Create Handover
            </h1>
            <p className="text-blue-100 text-lg">
              {formData.handoverType === 'pickup' 
                ? 'Vehicle Pickup Process' 
                : 'Vehicle Return Process'}
            </p>
          </motion.div>
          
          {rental && (
            <motion.div 
              variants={itemVariants}
              className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-blue-500"
            >
              <h2 className="font-semibold text-blue-800 mb-4 flex items-center">
                <FaClipboardList className="mr-2" /> 
                Rental Details
              </h2>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="flex items-start">
                  <FaCar className="mt-1 mr-2 text-blue-600" />
                  <div>
                    <div className="text-sm text-gray-500">Vehicle</div>
                    <div className="font-medium">{rental.carName}</div>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <FaUser className="mt-1 mr-2 text-blue-600" />
                  <div>
                    <div className="text-sm text-gray-500">Customer</div>
                    <div className="font-medium">{rental.customer.firstName} {rental.customer.lastName}</div>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <FaCalendarAlt className="mt-1 mr-2 text-blue-600" />
                  <div>
                    <div className="text-sm text-gray-500">Dates</div>
                    <div className="font-medium">
                      {moment(rental.pickupDateTime).format('DD/MM/YYYY')} - {moment(rental.returnDateTime).format('DD/MM/YYYY')}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          
          <motion.div 
            variants={itemVariants}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <div className="p-6 pb-0">
              <div className="relative mb-8">
                <div className="flex justify-between mb-2 relative z-10">
                  {[1, 2, 3, 4].map((step) => (
                    <motion.div 
                      key={step}
                      whileHover={{ scale: currentStep >= step ? 1.05 : 1 }}
                      onClick={() => currentStep >= step && setCurrentStep(step)}
                      className={`flex flex-col items-center cursor-pointer ${currentStep >= step ? "" : "opacity-60"}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md ${
                        currentStep === step
                          ? 'bg-blue-600 text-white'
                          : currentStep > step
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-700'
                      }`}>
                        {currentStep > step ? (
                          <FaCheckCircle className="w-5 h-5" />
                        ) : (
                          <span className="text-lg">{step}</span>
                        )}
                      </div>
                      <span className="text-xs mt-2 font-medium">
                        {step === 1 && 'Basic Info'}
                        {step === 2 && 'Vehicle Condition'}
                        {step === 3 && 'Documentation'}
                        {step === 4 && 'Signatures'}
                      </span>
                    </motion.div>
                  ))}
                </div>
                
                <div className="absolute top-5 left-0 w-full h-1 bg-gray-200 z-0">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${(currentStep - 1) * 100 / (totalSteps - 1)}%` }}
                    transition={{ duration: 0.3 }}
                    className="h-full bg-blue-600"
                  />
                </div>
              </div>
            </div>
            
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="p-6 pt-0"
              >
                <form onSubmit={handleSubmit}>
                  {renderStep()}
                  
                  <div className="mt-8 flex justify-between">
                    {currentStep > 1 && (
                      <motion.button
                        type="button"
                        onClick={prevStep}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-md flex items-center shadow-md"
                      >
                        <FaArrowLeft className="mr-2" />
                        Previous
                      </motion.button>
                    )}
                    
                    {currentStep < totalSteps ? (
                      <motion.button
                        type="button"
                        onClick={nextStep}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-md flex items-center shadow-md ml-auto"
                      >
                        Next
                        <FaArrowRight className="ml-2" />
                      </motion.button>
                    ) : (
                      <motion.button
                        type="submit"
                        disabled={loading}
                        whileHover={{ scale: loading ? 1 : 1.05 }}
                        whileTap={{ scale: loading ? 1 : 0.95 }}
                        className={`${loading ? 'bg-green-500' : 'bg-green-600 hover:bg-green-700'} text-white font-bold py-2 px-6 rounded-md flex items-center shadow-md ml-auto`}
                      >
                        {loading ? (
                          <>
                            <motion.div 
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              className="mr-2 h-5 w-5"
                            >
                              <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                            </motion.div>
                            Submitting
                          </>
                        ) : (
                          <>
                            <FaCheckCircle className="mr-2" />
                            Submit
                          </>
                        )}
                      </motion.button>
                    )}
                  </div>
                </form>
              </motion.div>
            </AnimatePresence>
          </motion.div>
            {showSuccessMessage && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 bg-green-50 border-l-4 border-green-500 p-6 rounded-lg shadow-md"
            >
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <FaCheckCircle className="h-6 w-6 text-green-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-green-800">
                    Success
                  </h3>
                  <div className="mt-2">                    <p className="text-green-700">
                      {showSuccessMessage === 'pickup' 
                        ? 'Vehicle pickup handover completed successfully. The rental status has been updated to "confirmed".' 
                        : 'Vehicle return handover completed successfully. The rental has been marked as "completed" and the vehicle mileage has been updated.'}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>          )}
        </motion.div>
      </div>

      {/* Add the SignatureModal component */}
      <SignatureModal
        isOpen={showSignatureModal}
        onClose={() => cancelSignature()}
        onSave={() => saveSignature()}
        onClear={() => clearSignature()}
        signingMode={signingMode}
        signaturePadRef={signingMode === 'employee' ? employeeSignatureRef : customerSignatureRef}
      />
    </div>
  );
}

export default HandoverForm;

// Signature Modal Component
const SignatureModal = ({ isOpen, onClose, onSave, onClear, signingMode, signaturePadRef }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative"
      >
        <button
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          onClick={onClose}
        >
          <FaTimesCircle className="text-xl" />
        </button>
        
        <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
          <FaPen className="mr-2 text-blue-600" />
          {signingMode === 'employee' ? 'Employee Signature' : 'Customer Signature'}
        </h3>
        
        <p className="text-gray-600 mb-4">
          Please sign in the space below
        </p>
        
        <div className="border-2 border-gray-300 rounded-lg mb-4">
          <SignatureCanvas
            ref={signaturePadRef}
            penColor="black"
            canvasProps={{
              className: "signature-canvas w-full h-40 cursor-crosshair", 
              style: { background: '#f9fafb' }
            }}
          />
        </div>
        
        <div className="flex justify-between">
          <button
            type="button"
            onClick={onClear}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 flex items-center"
          >
            <FaEraser className="mr-2" /> Clear
          </button>
          
          <button
            type="button"
            onClick={onSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <FaCheck className="mr-2" /> Save Signature
          </button>
        </div>
      </motion.div>
    </div>
  );
};